import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Lock, LogOut, Menu, ShieldCheck, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useSubscription } from "@/hooks/useSubscription";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useQueryClient } from "@tanstack/react-query";
import { NotificationBell } from "@/components/NotificationBell";
import { useDemoMode } from "@/hooks/useDemoMode";
import { clearStoredGrant } from "@/lib/demoGate";

// `flagKey` maps a nav entry / dropdown item to a feature_flags row. When the
// flag is disabled or marked coming_soon, the link is locked in the menu.
type DropdownItem = { label: string; flagKey?: string };
type DropdownGroup = { heading?: string; items: DropdownItem[] };
type NavItem = {
  label: string;
  to?: string;
  flagKey?: string;
  groups?: DropdownGroup[];
};

const navItems: NavItem[] = [
  {
    label: "Patricia I",
    groups: [
      {
        heading: "Drafting and Reviewing",
        items: [
          { label: "Affidavits", flagKey: "affidavit" },
          { label: "Letters of Demand", flagKey: "lod" },
          { label: "Resolutions", flagKey: "resolution" },
          { label: "Agreements", flagKey: "agreement" },
        ],
      },
    ],
  },
  { label: "Conditions Precedent Tracker", to: "/tools", flagKey: "cp" },
  { label: "Due Diligence", to: "/tools", flagKey: "dd" },
  { label: "Compliance Agent", to: "/compliance", flagKey: "compliance" },
  { label: "Tools", to: "/tools" },
  { label: "Pricing", to: "/pricing" },
];

export const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { isDemo } = useDemoMode();
  const { isAdmin } = useIsAdmin();
  const { tierLabel, isTrialing, trialDaysLeft } = useSubscription();
  const { flags } = useFeatureFlags();
  const queryClient = useQueryClient();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);

  // Returns true when a feature flag exists and is either disabled or marked
  // coming-soon. Items without a flagKey are always available.
  const isLocked = (flagKey?: string) => {
    if (!flagKey) return false;
    const flag = flags[flagKey];
    if (!flag) return false;
    return !flag.enabled || flag.coming_soon;
  };

  const lockedToast = (label: string) => {
    toast(`${label}: in development`, {
      description: "This feature isn't available yet. We'll notify you when it launches.",
    });
  };

  useEffect(() => {
    setMobileOpen(false);
    setAvatarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node)) {
        setAvatarOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const initials = (() => {
    const name = (user?.user_metadata?.full_name as string | undefined) ?? user?.email ?? "";
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return (name.slice(0, 2) || "ME").toUpperCase();
  })();

  const handleSignOut = async () => {
    await signOut();
    queryClient.clear();
    toast("Signed out", { description: "You have been signed out." });
    navigate("/tools");
  };

  const handleExitDemo = () => {
    clearStoredGrant();
    queryClient.clear();
    toast("Demo ended", { description: "Demo access cleared." });
    window.location.assign("/");
  };

  return (
    <nav className="sticky top-0 z-50 border-b border-hairline bg-background/95 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-content items-center px-8">
        <Link to="/tools" className="mr-10 flex flex-shrink-0 items-center gap-2.5">
          <span className="font-serif text-[1.3rem] font-extrabold tracking-tight text-foreground">
            Casey<span className="align-super text-[0.55em] font-medium">®</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <ul className="hidden h-full flex-1 items-stretch md:flex">
          {navItems.map((item) => {
            if (item.to) {
              const locked = isLocked(item.flagKey);
              if (locked) {
                return (
                  <li key={item.label} className="relative h-full">
                    <button
                      onClick={() => lockedToast(item.label)}
                      className="flex h-full items-center gap-1.5 px-[0.95rem] text-[0.85rem] font-medium text-muted-foreground/70 transition-colors hover:text-ink-mid"
                      title="In development"
                    >
                      {item.label}
                      <Lock className="h-2.5 w-2.5" strokeWidth={2} />
                    </button>
                  </li>
                );
              }
              return (
                <li key={item.label} className="relative h-full">
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      cn(
                        "flex h-full items-center px-[0.95rem] text-[0.85rem] font-medium transition-colors",
                        isActive
                          ? "border-b-2 border-foreground text-foreground"
                          : "text-ink-mid hover:text-foreground"
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                </li>
              );
            }
            return (
              <li key={item.label} className="group relative h-full">
                <button className="flex h-full items-center gap-1 px-[0.95rem] text-[0.85rem] font-medium text-ink-mid transition-colors hover:text-foreground">
                  {item.label}
                  <ChevronDown className="h-2.5 w-2.5 transition-transform group-hover:rotate-180" strokeWidth={2} />
                </button>
                <div className="invisible absolute left-0 top-full min-w-[230px] -translate-y-1.5 rounded-xl border border-hairline bg-background p-2 opacity-0 shadow-card-hover transition-all duration-150 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                  {item.groups?.map((g, gi) => (
                    <div key={gi}>
                      {g.heading && (
                        <div className="label-uppercase px-2.5 pb-0.5 pt-1.5">{g.heading}</div>
                      )}
                      {g.items.map((it) => {
                        const locked = isLocked(it.flagKey);
                        return (
                          <button
                            key={it.label}
                            type="button"
                            onClick={() => (locked ? lockedToast(it.label) : navigate("/tools"))}
                            className={cn(
                              "flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-[0.83rem] transition-colors",
                              locked
                                ? "text-muted-foreground/70 hover:bg-surface"
                                : "text-ink-mid hover:bg-surface hover:text-foreground"
                            )}
                          >
                            <span
                              className={cn(
                                "h-1 w-1 flex-shrink-0 rounded-full",
                                locked ? "bg-muted-foreground/40" : "bg-foreground"
                              )}
                            />
                            <span className="flex-1">{it.label}</span>
                            {locked && <Lock className="h-2.5 w-2.5" strokeWidth={2} />}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </li>
            );
          })}
        </ul>

        <div className="ml-auto flex items-center gap-2">
          {user ? (
            <>
              <div className="hidden md:block">
                <NotificationBell />
              </div>
              <div ref={avatarRef} className="relative hidden md:block">
              <button
                onClick={() => setAvatarOpen((v) => !v)}
                className="grid h-8 w-8 place-items-center rounded-full bg-forest font-serif text-[0.72rem] font-semibold text-bone"
                aria-label="Account menu"
              >
                {initials}
              </button>
              {avatarOpen && (
                <div className="absolute right-0 top-full mt-2 min-w-[220px] rounded-xl border border-hairline bg-background p-1.5 shadow-card-hover">
                  <div className="border-b border-hairline px-3 py-2">
                    <div className="truncate text-[0.8rem] font-medium text-foreground">
                      {(user.user_metadata?.full_name as string) ?? "Signed in"}
                    </div>
                    <div className="truncate text-[0.72rem] text-muted-foreground">{user.email}</div>
                    <div className="mt-1.5 flex items-center gap-1.5">
                      <span className="rounded-chip border border-hairline bg-surface px-1.5 py-0.5 text-[0.62rem] font-bold uppercase tracking-wider text-ink-mid">
                        {tierLabel}
                      </span>
                      {isTrialing && (
                        <span className="text-[0.65rem] text-muted-foreground">
                          {trialDaysLeft}d trial left
                        </span>
                      )}
                    </div>
                  </div>
                  <Link
                    to="/dashboard"
                    className="mt-1 flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-[0.83rem] text-ink-mid transition-colors hover:bg-surface hover:text-foreground"
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/matters"
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-[0.83rem] text-ink-mid transition-colors hover:bg-surface hover:text-foreground"
                  >
                    Matters
                  </Link>
                  <Link
                    to="/clients"
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-[0.83rem] text-ink-mid transition-colors hover:bg-surface hover:text-foreground"
                  >
                    Clients
                  </Link>
                  <Link
                    to="/documents"
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-[0.83rem] text-ink-mid transition-colors hover:bg-surface hover:text-foreground"
                  >
                    My documents
                  </Link>
                  <Link
                    to="/settings"
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-[0.83rem] text-ink-mid transition-colors hover:bg-surface hover:text-foreground"
                  >
                    Settings
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-[0.83rem] font-medium text-foreground transition-colors hover:bg-surface"
                    >
                      <ShieldCheck className="h-3.5 w-3.5 text-brass-dark" />
                      Admin console
                    </Link>
                  )}
                  <button
                    onClick={handleSignOut}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-[0.83rem] text-ink-mid transition-colors hover:bg-surface hover:text-foreground"
                  >
                    <LogOut className="h-3.5 w-3.5" />
                    Log out
                  </button>
                </div>
              )}
              </div>
            </>
          ) : isDemo ? (
            <div className="hidden items-center gap-2 md:inline-flex">
              <span className="inline-flex items-center gap-1.5 rounded-btn border border-hairline bg-card px-3 py-1.5 text-[0.78rem] font-medium text-muted-foreground">
                <Lock className="h-3 w-3" /> Preview mode
              </span>
              <button
                onClick={handleExitDemo}
                className="inline-flex items-center gap-1.5 rounded-btn border border-hairline px-3 py-1.5 text-[0.78rem] font-medium text-ink-mid transition-colors hover:bg-surface hover:text-foreground"
              >
                <LogOut className="h-3.5 w-3.5" /> Exit demo
              </button>
            </div>
          ) : (
            <>
              <Link
                to="/login"
                className="hidden rounded-btn px-3.5 py-1.5 text-[0.83rem] font-medium text-ink-mid transition-colors hover:bg-surface hover:text-foreground md:block"
              >
                Log in
              </Link>
              <Link
                to="/signup"
                className="hidden rounded-btn bg-forest px-4 py-1.5 text-[0.83rem] font-semibold text-bone transition-all hover:-translate-y-px hover:brightness-110 md:block"
              >
                Sign up free
              </Link>
            </>
          )}
          <button
            className="rounded-md p-2 text-ink-mid md:hidden"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="border-t border-hairline bg-background md:hidden">
          <div className="space-y-1 px-6 py-4">
            {navItems.map((item) => {
              if (item.to) {
                const locked = isLocked(item.flagKey);
                return (
                  <div key={item.label}>
                    {locked ? (
                      <button
                        onClick={() => lockedToast(item.label)}
                        className="flex w-full items-center justify-between rounded-md px-2 py-2 text-left text-sm font-medium text-muted-foreground/70 hover:bg-surface"
                      >
                        <span>{item.label}</span>
                        <Lock className="h-3 w-3" />
                      </button>
                    ) : (
                      <Link
                        to={item.to}
                        className="block rounded-md px-2 py-2 text-sm font-medium text-foreground hover:bg-surface"
                      >
                        {item.label}
                      </Link>
                    )}
                  </div>
                );
              }
              return (
                <div key={item.label}>
                  <details className="group">
                    <summary className="flex cursor-pointer list-none items-center justify-between rounded-md px-2 py-2 text-sm font-medium text-ink-mid">
                      {item.label}
                      <ChevronDown className="h-3 w-3 transition-transform group-open:rotate-180" />
                    </summary>
                    <div className="space-y-0.5 pb-1 pl-4">
                      {item.groups?.flatMap((g) => g.items).map((it) => {
                        const locked = isLocked(it.flagKey);
                        return (
                          <button
                            key={it.label}
                            type="button"
                            onClick={() => (locked ? lockedToast(it.label) : navigate("/tools"))}
                            className={cn(
                              "flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm hover:bg-surface",
                              locked ? "text-muted-foreground/70" : "text-ink-mid"
                            )}
                          >
                            <span>{it.label}</span>
                            {locked && <Lock className="h-3 w-3" />}
                          </button>
                        );
                      })}
                    </div>
                  </details>
                </div>
              );
            })}
            {user ? (
              <div className="space-y-1 pt-3">
                <div className="rounded-md bg-surface px-3 py-2">
                  <div className="truncate text-[0.8rem] font-medium text-foreground">
                    {(user.user_metadata?.full_name as string) ?? "Signed in"}
                  </div>
                  <div className="truncate text-[0.72rem] text-muted-foreground">{user.email}</div>
                </div>
                <div className="flex items-center justify-between rounded-md px-2 py-2">
                  <span className="text-[0.78rem] font-medium text-ink-mid">Notifications</span>
                  <NotificationBell />
                </div>
                <Link to="/dashboard" className="block rounded-md px-2 py-2 text-sm font-medium text-foreground hover:bg-surface">
                  Dashboard
                </Link>
                <Link to="/matters" className="block rounded-md px-2 py-2 text-sm font-medium text-foreground hover:bg-surface">
                  Matters
                </Link>
                <Link to="/clients" className="block rounded-md px-2 py-2 text-sm font-medium text-foreground hover:bg-surface">
                  Clients
                </Link>
                <Link to="/documents" className="block rounded-md px-2 py-2 text-sm font-medium text-foreground hover:bg-surface">
                  My documents
                </Link>
                <Link to="/settings" className="block rounded-md px-2 py-2 text-sm font-medium text-foreground hover:bg-surface">
                  Settings
                </Link>
                {isAdmin && (
                  <Link to="/admin" className="flex items-center gap-2 rounded-md px-2 py-2 text-sm font-semibold text-foreground hover:bg-surface">
                    <ShieldCheck className="h-3.5 w-3.5 text-brass-dark" />
                    Admin console
                  </Link>
                )}
                <button
                  onClick={handleSignOut}
                  className="mt-1 flex w-full items-center gap-2 rounded-btn border border-hairline px-3 py-2 text-sm font-medium text-foreground"
                >
                  <LogOut className="h-3.5 w-3.5" />
                  Log out
                </button>
              </div>
            ) : isDemo ? (
              <div className="space-y-2">
                <div className="flex items-center justify-center gap-1.5 rounded-btn border border-hairline bg-card px-3 py-2 text-[0.78rem] font-medium text-muted-foreground">
                  <Lock className="h-3 w-3" /> Preview mode, sign-in disabled
                </div>
                <button
                  onClick={handleExitDemo}
                  className="flex w-full items-center justify-center gap-2 rounded-btn border border-hairline px-3 py-2 text-sm font-medium text-foreground"
                >
                  <LogOut className="h-3.5 w-3.5" /> Exit demo
                </button>
              </div>
            ) : (
              <div className="flex gap-2 pt-3">
                <Link
                  to="/login"
                  className="flex-1 rounded-btn border border-hairline px-3 py-2 text-center text-sm font-medium text-foreground"
                >
                  Log in
                </Link>
                <Link
                  to="/signup"
                  className="flex-1 rounded-btn bg-forest px-3 py-2 text-center text-sm font-semibold text-bone"
                >
                  Sign up free
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};
